import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NAV } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';
import LanguageSwitcher from './LanguageSwitcher';
import KlymartLogo from './KlymartLogo';

export default function TopNav() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const scrollTo = (id) => (e) => {
    e?.preventDefault();
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Navigate to landing then scroll once mounted.
      navigate(`/#${id}`);
      // Small timeout to let the landing page mount before we scroll.
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  };

  return (
    <header
      data-testid={NAV.root}
      className="sticky top-0 z-40 w-full backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="group inline-flex items-center gap-2.5"
          aria-label={t('nav.home')}
        >
          <motion.span
            whileHover={{ rotate: -6 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="inline-flex"
          >
            <KlymartLogo size={32} />
          </motion.span>
          <span className="font-display text-lg sm:text-xl tracking-tight">
            Klymart<sup className="text-[10px] ml-0.5 text-muted-foreground">®</sup>
          </span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-3">
          <nav className="hidden md:flex items-center gap-5 text-sm text-muted-foreground">
            <a onClick={scrollTo('problem')} href="#problem" className="hover:text-foreground transition-colors cursor-pointer">{t('nav.problem')}</a>
            <a onClick={scrollTo('solution')} href="#solution" className="hover:text-foreground transition-colors cursor-pointer">{t('nav.solution')}</a>
            <a onClick={scrollTo('how-it-works')} href="#how-it-works" className="hover:text-foreground transition-colors cursor-pointer">{t('nav.howItWorks')}</a>
            <a onClick={scrollTo('point-of-purchase')} href="#point-of-purchase" className="hover:text-foreground transition-colors cursor-pointer">{t('nav.pop')}</a>
          </nav>
          <LanguageSwitcher />
          <Button
            onClick={scrollTo('waitlist')}
            data-testid={NAV.ctaWaitlist}
            className="h-9 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 px-4 font-medium"
          >
            {t('nav.joinWaitlist')}
            <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
